import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getKaraokeQueueItemStage } from '../../utils/karaokeRuntimeState';
import {
  forceRemoteJukeboxQueueItem,
  forceRemoteKaraokeQueueItem,
  getRemoteJukeboxRuntime,
  getRemoteKaraokeRuntime,
  launchRemoteJukeboxRuntime,
  launchRemoteKaraokeRuntime,
  runRemoteJukeboxRuntimeAction,
  runRemoteKaraokeRuntimeAction,
  searchRemoteJukeboxCatalog,
  searchRemoteKaraokeCatalog,
  stopRemoteJukeboxRuntime,
  stopRemoteKaraokeRuntime,
} from './deviceRemoteApi';

const TERMINAL_STATUSES = new Set(['rejected', 'played', 'skipped', 'expired']);

const getStatus = (item) => String(item?.status || '').toLowerCase();

const compareByPosition = (a, b) => {
  const positionDelta = Number(a.positionValue || 0) - Number(b.positionValue || 0);
  if (positionDelta !== 0) return positionDelta;
  return Number(a.id || 0) - Number(b.id || 0);
};

const titleCase = (value = '') =>
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());

const buildParticipantMap = (items = []) => new Map(items.map((item) => [Number(item.id), item]));

const normalizeVolume = (value, fallback = 100) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(100, Math.max(0, Math.round(parsed)));
};

const formatResolvedEnd = (lifecycle) => {
  if (!lifecycle?.resolvedEndAt) return 'Until stopped';
  const end = new Date(lifecycle.resolvedEndAt);
  if (Number.isNaN(end.getTime())) return 'Until stopped';
  try {
    return `Ends ${new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: lifecycle.sourceTimeZone || undefined,
      timeZoneName: 'short',
    }).format(end)}`;
  } catch (_error) {
    return `Ends ${end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  }
};

const RUNTIME_APIS = {
  karaoke: {
    key: 'karaoke',
    label: 'Karaoke',
    getRuntime: getRemoteKaraokeRuntime,
    searchCatalog: searchRemoteKaraokeCatalog,
    forceQueueItem: forceRemoteKaraokeQueueItem,
    runAction: runRemoteKaraokeRuntimeAction,
    launch: launchRemoteKaraokeRuntime,
    stop: stopRemoteKaraokeRuntime,
  },
  jukebox: {
    key: 'jukebox',
    label: 'Jukebox',
    getRuntime: getRemoteJukeboxRuntime,
    searchCatalog: searchRemoteJukeboxCatalog,
    forceQueueItem: forceRemoteJukeboxQueueItem,
    runAction: runRemoteJukeboxRuntimeAction,
    launch: launchRemoteJukeboxRuntime,
    stop: stopRemoteJukeboxRuntime,
  },
};

function KaraokeRemotePanel({
  areaId = null,
  compact = false,
  eventInstanceId = null,
  onRuntimeChange = null,
  plugin = 'karaoke',
}) {
  const runtimeApi = RUNTIME_APIS[plugin] || RUNTIME_APIS.karaoke;
  const [runtime, setRuntime] = useState(null);
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState(`Loading ${runtimeApi.label.toLowerCase()} queue...`);
  const [query, setQuery] = useState('');
  const [searchStatus, setSearchStatus] = useState('idle');
  const [searchResults, setSearchResults] = useState([]);
  const [acting, setActing] = useState(false);
  const [draggedQueueItemId, setDraggedQueueItemId] = useState(null);
  const [touchDragQueueItemId, setTouchDragQueueItemId] = useState(null);
  const [performerVolume, setPerformerVolume] = useState(100);
  const [backgroundVolume, setBackgroundVolume] = useState(100);

  const snapshot = runtime?.snapshot || null;
  const session = snapshot?.session || null;
  const queueItems = Array.isArray(snapshot?.queueItems) ? snapshot.queueItems : [];
  const participants = Array.isArray(snapshot?.participants) ? snapshot.participants : [];
  const participantMap = useMemo(() => buildParticipantMap(participants), [participants]);
  const activeQueueItemId = Number(session?.state?.runtime?.activeQueueItemId) || null;
  const runtimeState = session?.state?.runtime || {};
  const lifecycle = session?.state?.lifecycle || {};
  const settings = session?.state?.settings || {};
  const volumeSettings = settings.karaokeVolumes || settings.karaokeVolumeControls || {};
  const isRunning = Boolean(session && session.status !== 'paused');

  const liveQueue = useMemo(() => {
    const active = [];
    const upcoming = [];

    queueItems.forEach((item) => {
      const itemStatus = getStatus(item);
      if (TERMINAL_STATUSES.has(itemStatus)) return;

      if (Number(item.id) === activeQueueItemId) {
        active.push(item);
        return;
      }

      upcoming.push(item);
    });

    return [
      ...active.sort(compareByPosition),
      ...upcoming.sort(compareByPosition),
    ];
  }, [activeQueueItemId, queueItems]);

  const loadRuntime = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setStatus('loading');
        setMessage(`Loading ${runtimeApi.label.toLowerCase()} queue...`);
      }
      const data = await runtimeApi.getRuntime({ areaId, eventInstanceId });
      const nextRuntime = data?.[runtimeApi.key]?.runtime || null;
      setRuntime(nextRuntime);
      if (!nextRuntime) {
        setStatus('unavailable');
        setMessage(`No ${runtimeApi.label.toLowerCase()} event is configured for this area yet.`);
        return;
      }
      setStatus('ready');
      setMessage('');
    } catch (error) {
      setRuntime(null);
      setStatus(error?.status === 404 ? 'unavailable' : 'error');
      setMessage(error.message || `${runtimeApi.label} runtime is not available.`);
    }
  }, [areaId, eventInstanceId, runtimeApi]);

  useEffect(() => {
    loadRuntime();
    const timer = window.setInterval(() => loadRuntime({ silent: true }), 10000);
    return () => window.clearInterval(timer);
  }, [loadRuntime]);

  useEffect(() => {
    setPerformerVolume(normalizeVolume(volumeSettings.performer ?? volumeSettings.performerVolume, 100));
    setBackgroundVolume(normalizeVolume(volumeSettings.background ?? volumeSettings.backgroundVolume, 100));
  }, [
    volumeSettings.background,
    volumeSettings.backgroundVolume,
    volumeSettings.performer,
    volumeSettings.performerVolume,
  ]);

  const runAction = async (action, queueItemId = null, extra = {}) => {
    try {
      setActing(true);
      setMessage('');
      const result = await runtimeApi.runAction({
        action,
        areaId,
        eventInstanceId,
        ...(queueItemId ? { queueItemId } : {}),
        ...extra,
      });
      setRuntime(result);
      return result;
    } catch (error) {
      setMessage(error.message || `${runtimeApi.label} action failed.`);
      throw error;
    } finally {
      setActing(false);
    }
  };

  const handleApplyVolumes = async () => {
    try {
      await runAction('set_volumes', null, {
        performerVolume,
        backgroundVolume,
      });
      setMessage('Volume controls applied.');
    } catch {
      // Message is already set by runAction.
    }
  };

  const handleLaunch = async () => {
    try {
      setActing(true);
      setMessage('');
      const result = await runtimeApi.launch({ areaId, eventInstanceId });
      setRuntime({
        eventInstance: result.eventInstance,
        snapshot: result.snapshot,
        guestPath: result.guestPath,
        hasLiveSession: true,
      });
      setStatus('ready');
      onRuntimeChange?.({ action: 'launch', plugin: runtimeApi.key, eventInstanceId });
    } catch (error) {
      setMessage(error.message || `Failed to start ${runtimeApi.label.toLowerCase()}.`);
    } finally {
      setActing(false);
    }
  };

  const handleStop = async () => {
    try {
      setActing(true);
      setMessage('');
      const result = await runtimeApi.stop({ areaId, eventInstanceId });
      setRuntime(result);
      onRuntimeChange?.({ action: 'stop', plugin: runtimeApi.key, eventInstanceId });
    } catch (error) {
      setMessage(error.message || `Failed to stop ${runtimeApi.label.toLowerCase()}.`);
    } finally {
      setActing(false);
    }
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;

    try {
      setSearchStatus('loading');
      setMessage('');
      const result = await runtimeApi.searchCatalog({
        query: trimmedQuery,
        maxResults: 8,
        areaId,
        eventInstanceId,
      });
      const items = Array.isArray(result?.items) ? result.items : [];
      setSearchResults(items);
      setSearchStatus(items.length ? 'ready' : 'empty');
    } catch (error) {
      setSearchResults([]);
      setSearchStatus('error');
      setMessage(error.message || 'YouTube search failed.');
    }
  };

  const handleAddSong = async (item) => {
    if (!item) return;

    try {
      setSearchStatus('adding');
      await runtimeApi.forceQueueItem(item, { areaId, eventInstanceId });
      setMessage(`Added "${item.title || 'selected song'}" to the queue.`);
      setSearchResults([]);
      setQuery('');
      setSearchStatus('idle');
      await loadRuntime({ silent: true });
    } catch (error) {
      setSearchStatus('error');
      setMessage(error.message || 'Failed to add song to the queue.');
    }
  };

  const handleQueueDrop = async (targetQueueItemId, sourceOverride = null) => {
    const sourceQueueItemId = Number(sourceOverride || draggedQueueItemId || 0);
    const targetId = Number(targetQueueItemId || 0);
    setDraggedQueueItemId(null);
    if (!sourceQueueItemId || !targetId || sourceQueueItemId === targetId || acting) return;

    const fromIndex = liveQueue.findIndex((item) => Number(item.id) === sourceQueueItemId);
    const toIndex = liveQueue.findIndex((item) => Number(item.id) === targetId);
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;

    const action = fromIndex < toIndex ? 'move_down' : 'move_up';
    const steps = Math.abs(toIndex - fromIndex);

    try {
      setActing(true);
      setMessage('');
      let latestRuntime = null;
      for (let index = 0; index < steps; index += 1) {
        latestRuntime = await runtimeApi.runAction({
          action,
          areaId,
          eventInstanceId,
          queueItemId: sourceQueueItemId,
        });
      }
      if (latestRuntime) setRuntime(latestRuntime);
    } catch (error) {
      setMessage(error.message || 'Failed to reorder queue.');
      await loadRuntime({ silent: true });
    } finally {
      setActing(false);
    }
  };

  const handleTouchDragEnd = async (event) => {
    const sourceQueueItemId = Number(touchDragQueueItemId || 0);
    setTouchDragQueueItemId(null);
    if (!sourceQueueItemId || acting) return;

    const touch = event.changedTouches?.[0];
    if (!touch) return;

    const targetElement = document
      .elementFromPoint(touch.clientX, touch.clientY)
      ?.closest('[data-karaoke-queue-item-id]');
    const targetQueueItemId = Number(targetElement?.getAttribute('data-karaoke-queue-item-id') || 0);
    if (!targetQueueItemId || targetQueueItemId === sourceQueueItemId) return;

    await handleQueueDrop(targetQueueItemId, sourceQueueItemId);
  };

  const renderQueueItem = (item, index) => {
    const participant = participantMap.get(Number(item.submittedByParticipantId)) || null;
    const isActive = Number(item.id) === activeQueueItemId;
    const playbackStage = getKaraokeQueueItemStage(runtimeState, item.id);
    const requesterLabel = item.payload?.forcedByHost
      ? 'Host override'
      : participant?.nickname || 'Guest';

    return (
      <div
        key={item.id}
        className={`karaoke-remote__queue-row${isActive ? ' karaoke-remote__queue-row--active' : ''}`}
        data-karaoke-queue-item-id={item.id}
        onDragOver={(event) => event.preventDefault()}
        onDrop={() => handleQueueDrop(item.id)}
      >
        <button
          type="button"
          className="karaoke-remote__drag-handle"
          draggable={!acting}
          aria-label={`Drag ${item.label || 'song'} to reorder`}
          onDragStart={() => setDraggedQueueItemId(Number(item.id))}
          onTouchStart={() => setTouchDragQueueItemId(Number(item.id))}
          onTouchEnd={handleTouchDragEnd}
        >
          <span />
          <span />
          <span />
        </button>
        <div className="karaoke-remote__queue-position">
          {playbackStage === 'performing'
            ? 'Live'
            : playbackStage === 'pending'
              ? 'Starting'
              : playbackStage === 'prepared'
                ? 'Ready'
                : index + 1}
        </div>
        <div className="karaoke-remote__queue-main">
          <strong>{item.label || 'Untitled song'}</strong>
          <span>{requesterLabel}</span>
          <div className="karaoke-remote__row-actions">
            <button
              type="button"
              className="phone-remote__primary-btn karaoke-remote__small-btn"
              onClick={() => runAction('start_item', item.id)}
              disabled={acting}
            >
              Start
            </button>
            <button
              type="button"
              className="phone-remote__ghost-btn karaoke-remote__small-btn"
              onClick={() => runAction('mark_played', item.id)}
              disabled={acting}
            >
              Played
            </button>
            <button
              type="button"
              className="phone-remote__primary-btn karaoke-remote__small-btn karaoke-remote__cancel-btn"
              onClick={() => runAction('cancel_item', item.id)}
              disabled={acting}
            >
              Cancel
            </button>
          </div>
        </div>
        <div className="karaoke-remote__queue-status">
          {playbackStage === 'queued'
            ? titleCase(item.status || 'queued')
            : titleCase(playbackStage)}
        </div>
      </div>
    );
  };

  const renderSearchResult = (item) => {
    const tooLong = !!(item.tooLong || item.exceedsMaxDuration);
    const meta = [item.channelTitle || 'YouTube', item.durationText || null].filter(Boolean).join(' • ');
    return (
      <div key={item.videoId} className={`karaoke-remote__result${tooLong ? ' karaoke-remote__result--disabled' : ''}`}>
        <div className="karaoke-remote__thumb">
          {item.thumbnailUrl ? <img src={item.thumbnailUrl} alt="" loading="lazy" /> : null}
        </div>
        <div className="karaoke-remote__result-main">
          <strong>{item.title || 'YouTube result'}</strong>
          <span>{meta}</span>
          {tooLong ? (
            <span className="karaoke-remote__result-warning">
              Too long{item.maxDurationMinutes ? ` - ${item.maxDurationMinutes} minute limit` : ''}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          className="phone-remote__primary-btn karaoke-remote__add-btn"
          onClick={() => handleAddSong(item)}
          disabled={searchStatus === 'adding' || tooLong}
        >
          {tooLong ? 'Too Long' : 'Add'}
        </button>
      </div>
    );
  };

  if (status === 'loading') {
    return <div className="phone-remote__notice">{message}</div>;
  }

  if (status !== 'ready' || !session) {
    return (
      <section className="phone-remote__panel karaoke-remote__start-panel">
        <button type="button" className="phone-remote__primary-btn" onClick={handleLaunch} disabled={acting || status === 'loading'}>
          Start {runtimeApi.label}
        </button>
        {message ? <div className="phone-remote__notice">{message}</div> : null}
      </section>
    );
  }

  if (!isRunning) {
    return (
      <section className="phone-remote__panel karaoke-remote__start-panel">
        <button type="button" className="phone-remote__primary-btn" onClick={handleLaunch} disabled={acting}>
          Start {runtimeApi.label}
        </button>
        {message ? <div className="phone-remote__notice">{message}</div> : null}
      </section>
    );
  }

  return (
    <div className={`karaoke-remote${compact ? ' karaoke-remote--compact' : ''}`}>
      <section className="phone-remote__panel">
        <div className="karaoke-remote__header">
          <div>
            {!compact ? <span className="phone-remote__section-label">{runtimeApi.label} Queue</span> : null}
            {!compact ? <h2>{runtime?.eventInstance?.name || runtimeApi.label}</h2> : null}
            {!compact ? (
              <>
                <div className="karaoke-remote__meta">
                  {titleCase(session.status || 'live')} • {titleCase(session.currentPhase || 'queue_open')} • {titleCase(runtimeState.interstitialMode || 'ambient_video')}
                </div>
                <div className="karaoke-remote__meta">{formatResolvedEnd(lifecycle)}</div>
              </>
            ) : null}
          </div>
          <button type="button" className="phone-remote__ghost-btn" onClick={handleStop} disabled={acting}>
            Stop
          </button>
        </div>

        {!compact ? <div className="karaoke-remote__control-row">
          <button type="button" className="phone-remote__ghost-btn" onClick={() => runAction('queue_open')} disabled={acting}>
            Queue Open
          </button>
          <button type="button" className="phone-remote__ghost-btn" onClick={() => runAction('ambient')} disabled={acting}>
            Ambient
          </button>
          <button type="button" className="phone-remote__ghost-btn" onClick={() => runAction('silence')} disabled={acting}>
            Silence
          </button>
          {session.status === 'paused' ? (
            <button type="button" className="phone-remote__primary-btn" onClick={() => runAction('resume')} disabled={acting}>
              Resume
            </button>
          ) : (
            <button type="button" className="phone-remote__ghost-btn" onClick={() => runAction('pause')} disabled={acting}>
              Pause
            </button>
          )}
        </div> : null}

        <div className="karaoke-remote__volumes">
          <label>
            <span>{runtimeApi.key === 'jukebox' ? 'Playback' : 'Performer'} {performerVolume}%</span>
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={performerVolume}
              onChange={(event) => setPerformerVolume(normalizeVolume(event.target.value, performerVolume))}
            />
          </label>
          <label>
            <span>Background {backgroundVolume}%</span>
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={backgroundVolume}
              onChange={(event) => setBackgroundVolume(normalizeVolume(event.target.value, backgroundVolume))}
            />
          </label>
          <button type="button" className="phone-remote__primary-btn" onClick={handleApplyVolumes} disabled={acting}>
            Apply Volumes
          </button>
        </div>

        <div className="karaoke-remote__queue">
          {liveQueue.length ? (
            liveQueue.map(renderQueueItem)
          ) : (
            <div className="karaoke-remote__empty">No queued songs yet.</div>
          )}
        </div>
      </section>

      <section className="phone-remote__panel">
        {!compact ? <span className="phone-remote__section-label">Add Song</span> : null}
        <form className="karaoke-remote__search" onSubmit={handleSearch}>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search YouTube"
            className="karaoke-remote__search-input"
          />
          <button
            type="submit"
            className="phone-remote__primary-btn"
            disabled={!query.trim() || searchStatus === 'loading' || searchStatus === 'adding'}
          >
            {searchStatus === 'loading' ? 'Searching...' : 'Search'}
          </button>
        </form>

        {message ? <div className="phone-remote__notice">{message}</div> : null}
        {searchStatus === 'empty' ? <div className="phone-remote__notice">No YouTube results found.</div> : null}
        {searchResults.length ? (
          <div className="karaoke-remote__results">
            {searchResults.map(renderSearchResult)}
          </div>
        ) : null}
      </section>
    </div>
  );
}

export default KaraokeRemotePanel;
